import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import BackToTopButton from '../../components/feature/BackToTopButton';

const EmpresaPage = () => {
  useEffect(() => {
    document.title = 'Empresa - PLADUREX | Especialistas en Pladur en Mataró';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Conoce PLADUREX, tu empresa de confianza para instalación de pladur en Mataró. Profesionales con experiencia en tabiques, techos y aislamientos.'
      );
    }

    // Add WebPage Schema.org
    const existingWebPageSchema = document.querySelector('script[data-schema="webpage"]');
    if (existingWebPageSchema) existingWebPageSchema.remove();
    
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/empresa#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/empresa`,
      "name": "Empresa - PLADUREX | Especialistas en Pladur en Mataró",
      "description": "Conoce PLADUREX, tu empresa de confianza para instalación de pladur en Mataró. Profesionales con experiencia en tabiques, techos y aislamientos.",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "about": {
        "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
      }
    };
    
    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.setAttribute('data-schema', 'webpage');
    webPageScript.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(webPageScript);

    // Add BreadcrumbList Schema.org
    const existingSchema = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingSchema) existingSchema.remove();
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": import.meta.env.VITE_SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Empresa",
          "item": `${import.meta.env.VITE_SITE_URL}/empresa`
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'breadcrumb');
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (schemaScript) schemaScript.remove();
      const webPageSchemaScript = document.querySelector('script[data-schema="webpage"]');
      if (webPageSchemaScript) webPageSchemaScript.remove();
    };
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Profesionalidad',
      description: 'Equipo cualificado con años de experiencia en instalación de pladur',
    },
    {
      icon: 'ri-time-line',
      title: 'Puntualidad',
      description: 'Cumplimos plazos y horarios acordados sin retrasos',
    },
    {
      icon: 'ri-tools-line',
      title: 'Calidad',
      description: 'Materiales de primera calidad y acabados impecables',
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Atención',
      description: 'Asesoramiento personalizado en cada proyecto',
    },
  ];

  const workProcess = [
    {
      number: '01',
      title: 'Visita y Medición',
      description:
        'Nos desplazamos a tu vivienda, oficina o local en Mataró para tomar medidas exactas y analizar las necesidades del proyecto. Evaluamos el espacio, detectamos posibles complicaciones y te asesoramos sobre las mejores soluciones.',
    },
    {
      number: '02',
      title: 'Propuesta Detallada',
      description:
        'Elaboramos un presupuesto claro y sin letra pequeña que incluye materiales, mano de obra, transporte y limpieza. Te explicamos qué tipo de placas utilizaremos, el sistema de montaje y los plazos de ejecución.',
    },
    {
      number: '03',
      title: 'Ejecución de la Obra',
      description:
        'Instalamos con precisión y limpieza, respetando los horarios acordados. Trabajamos en viviendas ocupadas minimizando molestias. Protegemos suelos y muebles, y recogemos escombros diariamente.',
    },
    {
      number: '04',
      title: 'Remates y Entrega',
      description:
        'Realizamos los acabados finales: juntas perfectas, cantos limpios y superficies listas para pintar. Revisamos cada detalle antes de darte por finalizado el trabajo. Te entregamos factura y garantía.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F3F3F3]">
      <Header />
      <WhatsAppButton />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/672185b863f67e2aa0697707cd68810b.jpg"
            alt="Equipo PLADUREX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0B0B0F]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 break-words">
              <strong>Especialistas en pladur</strong>
              <span className="block mt-2 text-[#FF007A]">en Mataró y el Maresme</span>
            </h1>
            <p className="text-lg text-[#F3F3F3]/90 max-w-3xl mx-auto leading-relaxed break-words">
              Somos profesionales dedicados a la <strong>instalación de pladur en Mataró</strong> con años de experiencia en reformas de viviendas,
              oficinas y locales comerciales.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 break-words">
                Quiénes <span className="text-[#FF007A]">somos</span>
              </h2>
              <p className="text-[#F3F3F3]/70 mb-4 leading-relaxed break-words">
                PLADUREX es tu empresa de confianza para trabajos de pladur en Mataró, Maresme y Barcelona. Nos especializamos en <Link to="/servicios#tabiques" className="text-[#FF007A] hover:underline">tabiques</Link>,
                <Link to="/servicios#trasdosados" className="text-[#FF007A] hover:underline"> trasdosados</Link>, <Link to="/servicios#techos" className="text-[#FF007A] hover:underline">falsos techos</Link> y sistemas de <Link to="/servicios#acustico" className="text-[#FF007A] hover:underline">aislamiento acústico</Link> y <Link to="/servicios#termico" className="text-[#FF007A] hover:underline">térmico</Link>.
              </p>
              <p className="text-[#F3F3F3]/70 mb-4 leading-relaxed break-words">
                Trabajamos tanto en obra nueva como en reformas, adaptándonos a las necesidades de cada cliente. Desde una simple
                redistribución de espacios hasta proyectos completos de interiorismo con pladur. Consulta nuestros <Link to="/servicios" className="text-[#FF007A] hover:underline">servicios de pladur</Link>.
              </p>
              <p className="text-[#F3F3F3]/70 leading-relaxed break-words">
                Nuestro compromiso es ofrecer acabados limpios, obra rápida y un trato profesional en cada proyecto. Trabajamos con
                materiales de calidad y cumplimos los plazos acordados. ¿Tienes dudas? Visita nuestra sección de <Link to="/faq" className="text-[#FF007A] hover:underline">preguntas frecuentes</Link>.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-64 sm:h-80 lg:h-96 rounded-xl overflow-hidden w-full"
            >
              <img
                src="https://readdy.ai/api/search-image?query=professional%20drywall%20installer%20working%20on%20interior%20wall%20installation%20with%20precision%20tools%20modern%20construction%20workspace%20dark%20atmospheric%20lighting%20clean%20professional%20quality%20work&width=800&height=600&seq=empresa-about-004&orientation=landscape"
                alt="Trabajo profesional de pladur"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 break-words">
              Nuestros <span className="text-[#FF007A]">valores</span>
            </h2>
            <p className="text-base sm:text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              Lo que nos define como profesionales del pladur en Mataró
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0B0B0F] rounded-xl p-6 text-center border border-[#FF007A]/10"
              >
                <div className="w-16 h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-2xl text-[#FF007A]`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 break-words">{value.title}</h3>
                <p className="text-[#F3F3F3]/70 text-sm leading-relaxed break-words">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 break-words">
              Cómo <span className="text-[#FF007A]">trabajamos</span>
            </h2>
            <p className="text-base sm:text-lg text-[#F3F3F3]/70 max-w-2xl mx-auto break-words">
              Nuestro proceso de trabajo garantiza resultados profesionales y sin sorpresas
            </p>
          </motion.div>

          <div className="space-y-8">
            {workProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-[#12121A] rounded-xl p-6 sm:p-8 border border-[#FF007A]/10"
              >
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007A]/10 rounded-full flex items-center justify-center border-2 border-[#FF007A]">
                    <span className="text-xl sm:text-2xl font-bold text-[#FF007A]">{step.number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-2xl font-semibold mb-3 break-words">{step.title}</h3>
                    <p className="text-[#F3F3F3]/70 leading-relaxed break-words">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-64 sm:h-80 lg:h-96 rounded-xl overflow-hidden w-full"
            >
              <img
                src="https://readdy.ai/api/search-image?query=clean%20organized%20construction%20workspace%20with%20drywall%20materials%20and%20professional%20tools%20modern%20interior%20renovation%20dark%20atmospheric%20lighting%20quality%20craftsmanship%20details&width=800&height=600&seq=empresa-quality-005&orientation=landscape"
                alt="Materiales de calidad"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 break-words">
                Materiales y <span className="text-[#FF007A]">calidad</span>
              </h2>
              <p className="text-[#F3F3F3]/70 mb-4 leading-relaxed break-words">
                Trabajamos con placas de pladur de primera calidad: estándar, <Link to="/servicios#antihumedad" className="text-[#FF007A] hover:underline">antihumedad (WA)</Link> para baños y cocinas, y con
                refuerzo acústico cuando el proyecto lo requiere.
              </p>
              <p className="text-[#F3F3F3]/70 mb-4 leading-relaxed break-words">
                Utilizamos perfilería metálica robusta, aislantes de lana de roca o lana mineral, y materiales de tratamiento de
                juntas profesionales para garantizar acabados perfectos. Descubre más sobre nuestro <Link to="/servicios#acustico" className="text-[#FF007A] hover:underline">aislamiento acústico</Link>.
              </p>
              <p className="text-[#F3F3F3]/70 leading-relaxed break-words">
                Todos nuestros trabajos quedan listos para pintar, con superficies completamente lisas y juntas invisibles. <Link to="/contacto" className="text-[#FF007A] hover:underline">Solicita tu presupuesto</Link> sin compromiso.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-center break-words">
              Trabajamos en <span className="text-[#FF007A]">viviendas ocupadas</span>
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-[#F3F3F3]/70 mb-4 leading-relaxed text-center break-words">
                Sabemos que muchas reformas se hacen mientras vives en casa. Por eso nos adaptamos a tus horarios y minimizamos
                las molestias:
              </p>
              <ul className="space-y-3 mt-8">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#FF007A] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-[#F3F3F3]/70 break-words">Protegemos suelos, muebles y zonas que no se tocan</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#FF007A] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-[#F3F3F3]/70 break-words">Recogemos escombros y limpiamos al final de cada jornada</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#FF007A] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-[#F3F3F3]/70 break-words">Coordinamos horarios para no interferir en tu día a día</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#FF007A] text-xl mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-[#F3F3F3]/70 break-words">
                    Obra seca: sin agua, sin escombros pesados, sin polvo excesivo
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-[#12121A] to-[#0B0B0F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              ¿Hablamos de tu <span className="text-[#FF007A]">proyecto?</span>
            </h2>
            <p className="text-lg text-[#F3F3F3]/70 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros para una visita sin compromiso. Te asesoramos y preparamos un presupuesto ajustado a tus
              necesidades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="px-8 py-4 bg-[#FF007A] text-white rounded-lg text-base font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                629 501 941
              </a>
              <Link
                to="/contacto"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                Formulario de Contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BackToTopButton />
    </div>
  );
};

export default EmpresaPage;
